import { useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { Button } from "design-system/atoms/Button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "design-system/atoms/Sheet"
import { Skeleton } from "design-system/atoms/Skeleton"
import DateDisplay from "design-system/molecules/DateDisplay"
import { getBackofficeTopicConversationsQuery } from "modules/backoffice/lib/knowledge/getBackofficeTopicConversationsQuery"
import { useOpenBackofficeConversation } from "modules/backoffice/lib/useOpenBackofficeConversation"
import { useTranslation } from "react-i18next"

export function BackofficeTopicConversationsSheet({
  topicId,
  onClose,
}: {
  topicId: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const query = useQuery(getBackofficeTopicConversationsQuery(topicId))
  const openConversation = useOpenBackofficeConversation()
  const data = query.data
  const isExpired = axios.isAxiosError(query.error) && query.error.response?.status === 404

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <SheetHeader className="shrink-0 border-b p-5 text-left">
          <SheetTitle>
            {data?.question ?? t("backoffice.knowledge.topicConversations.title")}
          </SheetTitle>
          <SheetDescription>
            {data
              ? t("backoffice.knowledge.topicConversations.companyDescription", {
                  company: data.companyName,
                })
              : t("backoffice.knowledge.topicConversations.description")}
          </SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
          {query.isPending && <Skeleton className="h-24 w-full" />}
          {query.isError && (
            <div role="alert" className="space-y-2">
              <p>
                {t(`backoffice.knowledge.topicConversations.${isExpired ? "expired" : "error"}`)}
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  if (isExpired) {
                    void queryClient.invalidateQueries({
                      queryKey: ["backofficeUnansweredQuestionTopics"],
                    })
                    onClose()
                  } else {
                    void query.refetch()
                  }
                }}
              >
                {t(
                  isExpired
                    ? "backoffice.knowledge.topicConversations.close"
                    : "backoffice.knowledge.categories.retry",
                )}
              </Button>
            </div>
          )}
          {data && !data.hasSupportingQuestions && (
            <p>{t("backoffice.knowledge.topicConversations.needsRecalculation")}</p>
          )}
          {data?.hasSupportingQuestions && !data.conversations.length && (
            <p>{t("backoffice.knowledge.topicConversations.empty")}</p>
          )}
          {openConversation.isError && (
            <p role="alert" className="text-sm text-destructive">
              {t("backoffice.knowledge.topicConversations.openError")}
            </p>
          )}
          {data && data.conversations.length > 0 && (
            <ul className="divide-y rounded-md border">
              {data.conversations.map((conversation) => (
                <li key={conversation.conversationId}>
                  <button
                    type="button"
                    className="w-full space-y-2 p-4 text-left hover:bg-muted/50 disabled:opacity-50"
                    disabled={openConversation.isPending}
                    onClick={() =>
                      openConversation.mutate({
                        ...conversation,
                        organizationId: data.organizationId,
                      })
                    }
                  >
                    <p className="text-sm">{conversation.summary}</p>
                    <span className="text-xs text-muted-foreground">
                      <DateDisplay date={conversation.createdAt} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
